import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontFamily, FontSize } from "config/font";
import { Color } from "config/colors";
import "react-tooltip/dist/react-tooltip.css";
import { Text } from "components/Typography/text.styled";
import {
  CircularProgressWrapper,
  Row,
} from "../../calendar/weeklyCalendar.styled";
import { CircularProgress, Modal } from "@material-ui/core";
import { useGetGig } from "hooks/useGetGig";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import {
  LinkWrapper,
  ModalBody,
  RowWrapper,
  TagsContainer,
} from "./gigDetail.styled";
import { Instant, LocalDateTime, ZoneId } from "@js-joda/core";
import { WorkerWithUrl } from "model/Company";
import { GigType, PaymentMethod } from "model/Gig";
import { Logo } from "components/nav/logo/logo.styled";
import { Column } from "../../applicantDetails/applicantDetails.styled";
import { GigDetailApplicants } from "./gigDetailApplicants.compontent";
import { GigDetailButtons } from "./gigDetailButtons.component";
import { GigDetailCosts } from "./gigDetailCosts.component";
import { GigDetailDescription } from "./gigDetailDescription";
import { GigDetailRatings } from "./gigDetailRatings.component";
import { GigDetailSkills } from "./gigDetailSkills.component";
import { GigDetailShifts } from "./gigDetailShifts.component";
import { GigDetailWorkers } from "./gigDetailWorkers.component";

type GigDetailsModalProps = {
  gigId: number;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalOpen: boolean;
  workerWithImageUrl?: WorkerWithUrl[];
};

export const GigDetailsModal: React.FC<GigDetailsModalProps> = ({
  gigId,
  setModalOpen,
  modalOpen,
}) => {
  const [gigData, isLoading, error, fetchGig] = useGetGig(gigId);
  const { t } = useTranslation();

  const zone = ZoneId.systemDefault();

  const startTimeGig = useMemo<LocalDateTime | undefined>(() => {
    const s = gigData?.shifts?.[0]?.startTime;
    if (!s) return undefined;
    try {
      return LocalDateTime.ofInstant(Instant.parse(s), zone);
    } catch {
      return undefined;
    }
  }, [gigData, zone]);

  const endTimeGig = useMemo<LocalDateTime | undefined>(() => {
    const e =
      gigData?.shifts?.[gigData?.shifts?.length ? gigData.shifts.length - 1 : 0]
        ?.endTime;
    if (!e) return undefined;
    try {
      return LocalDateTime.ofInstant(Instant.parse(e), zone);
    } catch {
      return undefined;
    }
  }, [gigData, zone]);

  const now = useMemo(() => LocalDateTime.now(), []);

  const isOngoing =
    !!startTimeGig &&
    !!endTimeGig &&
    now.isAfter(startTimeGig) &&
    now.isBefore(endTimeGig);

  const isPassed = !!endTimeGig && now.isAfter(endTimeGig);

  useEffect(() => {
    if (error) {
      setModalOpen(false);
    }
  }, [error, setModalOpen]);

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      aria-labelledby="gig-details-title"
      aria-describedby="gig-details-description"
    >
      <div>
        <ModalBody>
          {isLoading ? (
            <CircularProgressWrapper>
              <CircularProgress />
            </CircularProgressWrapper>
          ) : (
            <>
              <div>
                <LinkWrapper>
                  <TagsContainer>
                    {isOngoing && (
                      <Text
                        fontSize={FontSize.Small}
                        color={Color.BurntSienna}
                        backgroundColor={Color.Positive}
                        padding
                        style={{
                          margin: 0,
                          lineHeight: FontSize.Small,
                          width: 70,
                          marginBottom: 10,
                        }}
                      >
                        {t("GigsCompany.Ongoing")}
                      </Text>
                    )}
                    {isPassed && (
                      <Text
                        fontSize={FontSize.Small}
                        color={Color.Destructive}
                        backgroundColor={Color.LightWarning}
                        padding
                        style={{
                          margin: 0,
                          lineHeight: FontSize.Small,
                          width: 70,
                          marginBottom: 10,
                          textAlign: "center",
                        }}
                      >
                        {t("GigsCompany.Passed")}
                      </Text>
                    )}
                    <Text
                      fontSize={FontSize.Small}
                      color={Color.DarkGrey}
                      backgroundColor={Color.Disabled}
                      padding
                      style={{
                        margin: 0,
                        lineHeight: FontSize.Small,
                        width:
                          gigData?.type === GigType.STAFFING ||
                          gigData?.type === GigType.WORKPOOL
                            ? 90
                            : gigData?.type === GigType.AUTOGENERATED
                            ? 120
                            : 60,
                        marginBottom: 10,
                        textAlign: "center",
                        marginLeft: 10,
                      }}
                    >
                      {t(`GigsCompany.${gigData?.type}`)}
                    </Text>
                    {gigData?.paymentType === PaymentMethod.SIMFLEXITY_PAY && (
                      <Logo
                        src={SimflexityPayLogo}
                        style={{
                          height: "18px",
                          width: "auto",
                          marginLeft: 10,
                          marginTop: 2,
                        }}
                        alt="logo"
                      />
                    )}
                  </TagsContainer>
                  <CustomIcon
                    color={Color.LighterDestructive}
                    onClick={() => {
                      setModalOpen(false);
                    }}
                    name={"cross"}
                  >
                    {t("General.Close")}
                  </CustomIcon>
                </LinkWrapper>

                <Row>
                  <Text
                    id="gig-details-title"
                    fontSize={FontSize.H3}
                    fontFamily={FontFamily.MontserratRegular}
                    style={{ margin: 0 }}
                  >
                    {gigData?.role}
                  </Text>
                </Row>
                <Row id="gig-details-description">
                  <Column>
                    {gigData &&
                      gigData.applications &&
                      (gigData.type === GigType.AUTOGENERATED ||
                        gigData.type === GigType.SOLO) && (
                        <Text
                          fontSize={FontSize.Standard}
                          fontFamily={FontFamily.MontserratSemiBold}
                          style={{ margin: 0 }}
                        >
                          {`${gigData.applications[0].worker.firstName} ${gigData.applications[0].worker.lastName}`}
                        </Text>
                      )}
                    <Text
                      fontSize={FontSize.Small}
                      fontFamily={FontFamily.MontserratRegular}
                      style={{ margin: 0 }}
                    >
                      {`${gigData?.structure.companyEntityName} - ${gigData?.structure.companyUnitName} - ${gigData?.structure.workGroupName}`}
                    </Text>
                  </Column>
                </Row>
              </div>

              {gigData && (
                <RowWrapper>
                  {gigData.type === GigType.PUBLIC && (
                    <>
                      {gigData.skills.length >= 1 && (
                        <GigDetailSkills gigData={gigData} />
                      )}
                      <GigDetailCosts gigData={gigData} />
                    </>
                  )}

                  {gigData.type !== GigType.SOLO &&
                    gigData.type !== GigType.AUTOGENERATED && (
                      <>
                        <GigDetailWorkers gigData={gigData} />
                        <GigDetailApplicants gigData={gigData} />
                      </>
                    )}

                  {gigData.description && (
                    <GigDetailDescription gigData={gigData} />
                  )}
                  <GigDetailShifts gigData={gigData} />
                  <GigDetailRatings gigData={gigData} />
                  <GigDetailButtons
                    gigData={gigData}
                    onGigClosed={() => fetchGig(gigData.id)}
                    onGigUpdated={() => fetchGig(gigData.id)}
                  />
                </RowWrapper>
              )}
            </>
          )}
        </ModalBody>
      </div>
    </Modal>
  );
};
